import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { apiService } from "../../../core/api/index";
import { User, UserRole } from "../../../core/models/user";
import {
  VITE_API_CLIENT_ID,
  VITE_API_CLIENT_SECRET,
  VITE_API_URL,
} from "@/core/constants/env";

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  token_type: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  token_type: string;
}

export interface UserSettings {
  uid: number;
  email: string;
}

export class AuthService {
  static async login(
    username: string,
    password: string,
  ): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>(
      API_ENDPOINTS.AUTH.LOGIN,
      {
        username,
        password,
        grant_type: "password",
        client_id: VITE_API_CLIENT_ID,
        client_secret: VITE_API_CLIENT_SECRET,
      },
    );

    return response;
  }

  static async refreshToken(
    refreshToken: string,
  ): Promise<RefreshTokenResponse> {
    const response = await apiService.post<RefreshTokenResponse>(
      "/auth/refresh",
      {
        refresh_token: refreshToken,
        grant_type: "refresh_token",
      },
    );

    return response;
  }

  static async getUserProfile(): Promise<User> {
    try {
      // console.log("getUserProfile called - returning mock user");
      return Promise.resolve({
        id: "user-1",
        username: "stam",
        email: "gstam@rocketpath.com",
        firstName: "George",
        lastName: "Stamoulakis",
        organization: "Freelancer",
        role: UserRole.USER,
        isVerified: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as User);
    } catch (error) {
      console.error("getUserProfile error:", error);
      throw error;
    }
  }

  static async getUserSettings(): Promise<UserSettings> {
    try {
      const response = await apiService.get<UserSettings>(
        API_ENDPOINTS.USER.SETTINGS,
      );
      // console.log("getUserSettings response:", response);
      // localStorage.setItem("userSettings", JSON.stringify(response));
      return response;
    } catch (error) {
      console.error("getUserSettings error:", error);
      // console.log("Returning mock user settings");
      return {
        uid: 5,
        email: "current.email@example.com",
      };
    }
  }

  static async requestPasswordReset(email: string): Promise<void> {
    await apiService.post("/auth/password/reset-request", { email });
  }

  static async resetPassword(token: string, password: string): Promise<void> {
    await apiService.post("/auth/password/reset", { token, password });
  }

  static async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    await apiService.put("/auth/password/change", {
      currentPassword,
      newPassword,
    });
  }

  static async verifyEmail(token: string): Promise<void> {
    await apiService.post("/auth/email/verify", { token });
  }

  static async resendVerificationEmail(): Promise<void> {
    await apiService.post("/auth/email/resend-verification");
  }

  // ✅ GOOGLE LOGIN
  static async getGoogleUser(accessToken: string): Promise<any> {
    try {
      const response = await fetch(
        `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${accessToken}`,
      );
      const userData = await response.json();
      // console.log("Google user data:", userData);
      return userData;
    } catch (error) {
      console.error("Error fetching Google user:", error);
      throw error;
    }
  }

  static async googleLogin(
    obj: { access_token: string; expires_in: number },
    userData: { id: string },
  ): Promise<LoginResponse> {
    const data = new FormData();
    data.append("google_access_token", obj.access_token);
    data.append("google_user_id", userData.id);
    data.append("google_token_expires_in", obj.expires_in.toString());
    data.append("grant_type", "google_login_grant");
    data.append("client_id", VITE_API_CLIENT_ID);
    data.append("client_secret", VITE_API_CLIENT_SECRET);

    const response = await apiService.post<LoginResponse>(
      `${API_ENDPOINTS.AUTH.LOGIN}?_format=json`,
      data,
    );
    // console.log("Google login response:", response);
    return response;
  }

  // ✅ FACEBOOK LOGIN
  static async getFacebookUser(accessToken: string): Promise<any> {
    try {
      const response = await fetch(
        `https://graph.facebook.com/v25.0/me?fields=id,name,email,picture&access_token=${accessToken}`,
      );
      const userData = await response.json();
      // console.log("Facebook user data:", userData);
      return userData;
    } catch (error) {
      console.error("Error fetching Facebook user:", error);
      throw error;
    }
  }

  static async facebookLogin(
    obj: { access_token: string; expires_in: number },
    userData: { id: string },
  ): Promise<LoginResponse> {
    const data = new FormData();
    data.append("facebook_access_token", obj.access_token);
    data.append("facebook_user_id", userData.id);
    data.append("facebook_token_expires_in", obj.expires_in.toString());
    data.append("grant_type", "facebook_login_grant");
    data.append("client_id", VITE_API_CLIENT_ID);
    data.append("client_secret", VITE_API_CLIENT_SECRET);

    const response = await apiService.post<LoginResponse>(
      `${API_ENDPOINTS.AUTH.LOGIN}?_format=json`,
      data,
    );
    // console.log("Facebook login response:", response);
    return response;
  }

  // ✅ LINKEDIN LOGIN - Send code directly to backend
  static async linkedinLogin(code: string): Promise<LoginResponse> {
    // console.log("Sending LinkedIn code to backend:", code);

    const data = new FormData();
    data.append("linkedin_code", code);
    data.append("grant_type", "linkedin_login_grant");
    data.append("client_id", VITE_API_CLIENT_ID);
    data.append("client_secret", VITE_API_CLIENT_SECRET);

    const response = await apiService.post<LoginResponse>(
      `${API_ENDPOINTS.AUTH.LOGIN}?_format=json`,
      data,
    );
    // console.log("LinkedIn login response:", response);
    return response;
  }
}
